import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { PAYMENT_STATUS } from '../services/paymentService';
import LoadingSpinner from '../components/LoadingSpinner';

const AgentDetailsManagement = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [agentReferrals, setAgentReferrals] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    commissionRate: 10
  });
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchAgentDetails();
  }, [agentId]);

  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch agent data
      const usersSnap = await getDocs(collection(db, 'users'));
      const agentDoc = usersSnap.docs.find(doc => doc.id === agentId);
      
      if (!agentDoc) {
        alert('Agent not found');
        navigate('/superadmin/dashboard');
        return;
      }

      const agentData = { id: agentDoc.id, ...agentDoc.data() };
      setAgent(agentData);
      
      // Set edit form with current data
      setEditForm({
        firstName: agentData.firstName || '',
        lastName: agentData.lastName || '',
        email: agentData.email || '',
        phone: agentData.phone || '',
        address: agentData.address || '',
        commissionRate: agentData.commissionRate || 10
      });

      // Fetch referrals for this agent
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('referredBy', '==', agentData.uid || agentData.id));
      const querySnapshot = await getDocs(q);
      
      // Fetch referrals with their payment data
      const referralsWithPayments = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const userData = { id: doc.id, ...doc.data() };
          
          // Fetch successful payments for this user
          const paymentsQuery = query(
            collection(db, 'payments'),
            where('userId', '==', doc.id),
            where('status', '==', PAYMENT_STATUS.SUCCESS)
          );
          const paymentsSnap = await getDocs(paymentsQuery);
          
          const totalRevenue = paymentsSnap.docs.reduce((sum, paymentDoc) => {
            const payment = paymentDoc.data();
            return sum + (payment.totalAmount || payment.amount || 0);
          }, 0);
          
          const commission = (totalRevenue * (agentData.commissionRate || 10)) / 100;
          
          return {
            ...userData,
            totalRevenue,
            commission,
            paymentsCount: paymentsSnap.size
          };
        })
      );
      
      setAgentReferrals(referralsWithPayments);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching agent details:', error);
      setLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    setUpdateError('');
    setUpdateSuccess('');

    try {
      const agentRef = doc(db, 'users', agentId);
      await updateDoc(agentRef, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        address: editForm.address,
        commissionRate: parseFloat(editForm.commissionRate) || 10,
        updatedAt: new Date()
      });

      setUpdateSuccess('Agent details updated successfully!');
      setIsEditing(false);
      
      // Refresh agent data
      await fetchAgentDetails();
      
      setTimeout(() => setUpdateSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating agent:', error);
      setUpdateError(error.message || 'Failed to update agent');
    }
  };

  const handleDeleteAgent = async () => {
    try {
      const agentRef = doc(db, 'users', agentId);
      await deleteDoc(agentRef);
      alert('Agent deleted successfully');
      navigate('/superadmin/dashboard');
    } catch (error) {
      console.error('Error deleting agent:', error);
      alert('Failed to delete agent: ' + error.message);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!agent) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Agent not found</h3>
          <button
            onClick={() => navigate('/superadmin/dashboard')}
            className="mt-6 inline-flex w-full sm:w-auto justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-3">
        <button
          onClick={() => navigate('/superadmin/dashboard')}
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Agent Management</h1>
        <p className="text-gray-600 text-sm sm:text-base">Manage agent profile and view referral details</p>
      </div>

      {/* Success/Error Messages */}
      {updateSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div className="text-green-700 text-sm font-medium">{updateSuccess}</div>
        </div>
      )}

      {updateError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-red-700 text-sm font-medium">{updateError}</div>
        </div>
      )}

      {/* Agent Profile Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-3xl">
              {agent.firstName?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {agent.firstName} {agent.lastName}
              </h2>
              <p className="text-gray-600 text-sm sm:text-base break-words">{agent.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs sm:text-sm font-semibold">
                🤝 Agent
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Profile
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Agent
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    firstName: agent.firstName || '',
                    lastName: agent.lastName || '',
                    email: agent.email || '',
                    phone: agent.phone || '',
                    address: agent.address || '',
                    commissionRate: agent.commissionRate || 10
                  });
                }}
                className="inline-flex justify-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {isEditing ? (
          <form onSubmit={handleUpdateAgent} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={editForm.firstName}
                  onChange={handleEditFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={editForm.lastName}
                  onChange={handleEditFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email (Cannot be changed)</label>
              <input
                type="email"
                value={editForm.email}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={editForm.phone}
                onChange={handleEditFormChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
              <textarea
                name="address"
                value={editForm.address}
                onChange={handleEditFormChange}
                required
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Commission Rate (%)</label>
              <input
                type="number"
                name="commissionRate"
                value={editForm.commissionRate}
                onChange={handleEditFormChange}
                required
                min="0"
                max="100"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                className="inline-flex justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
              >
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
              <p className="text-gray-900">{agent.phone || 'Not provided'}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Commission Rate</label>
              <p className="text-gray-900">{agent.commissionRate || 10}%</p>
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
              <p className="text-gray-900 break-words">{agent.address || 'Not provided'}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Joined Date</label>
              <p className="text-gray-900">{agent.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Last Updated</label>
              <p className="text-gray-900">{agent.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Referrals Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Referrals</h2>
          {agentReferrals.length > 0 && (
            <p className="text-gray-500 text-sm sm:text-base">Comprehensive overview of this agent's performance</p>
          )}
        </div>

        {agentReferrals.length > 0 ? (
          <div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-indigo-200">
                <p className="text-sm text-indigo-600 font-medium">Total Referrals</p>
                <p className="text-2xl sm:text-3xl font-bold text-indigo-900">{agentReferrals.length}</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                <p className="text-sm text-emerald-600 font-medium">Total Revenue</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-900">
                  ₹{agentReferrals.reduce((sum, user) => sum + user.totalRevenue, 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                <p className="text-sm text-amber-600 font-medium">Total Commission ({agent.commissionRate || 10}%)</p>
                <p className="text-2xl sm:text-3xl font-bold text-amber-900">
                  ₹{agentReferrals.reduce((sum, user) => sum + user.commission, 0).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="min-w-full sm:min-w-0">
                <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 min-w-[140px]">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Revenue</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {agentReferrals.map((user, index) => (
                    <tr key={user.id} className={`border-b border-gray-100 hover:bg-indigo-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                            {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-700">{user.email}</td>
                      <td className="py-4 px-4 min-w-[140px]">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          user.userType === 'jobseeker' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {user.userType === 'jobseeker' ? '👤 Job Seeker' : '🏢 Employer'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-bold text-emerald-600">₹{user.totalRevenue.toLocaleString()}</span>
                        <span className="text-xs text-gray-500 ml-2">({user.paymentsCount} payments)</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-bold text-amber-600">₹{user.commission.toLocaleString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No referrals yet</h3>
            <p className="text-gray-500 text-sm sm:text-base">This agent hasn't referred any users yet.</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete Agent?</h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Are you sure you want to delete {agent.firstName} {agent.lastName}? This action cannot be undone.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAgent}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDetailsManagement;
